/**
 * WhatsApp Link Sharing Service
 * Creates shareable invoice/estimate links and sends them via WhatsApp
 */
class WhatsAppLinkService {
  static API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
  static FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL || '';

  /**
   * Share invoice via WhatsApp link
   * @param {Object} invoiceData - Complete invoice data
   * @param {string} phoneNumber - Customer phone number
   */
  static async shareInvoice(invoiceData, phoneNumber) {
    try {
      // Validate phone number
      if (!phoneNumber || phoneNumber.trim() === '') {
        alert('Please enter a phone number to send via WhatsApp');
        return;
      }

      // Create shareable link
      const linkData = await this.createShareableLink('invoice', invoiceData);
      
      if (!linkData.success) {
        throw new Error('Failed to create shareable link');
      }

      // Generate WhatsApp message with link
      const message = this.generateInvoiceMessage(invoiceData, linkData.shareableUrl);

      // Open WhatsApp with correct country code based on currency
      const formattedPhone = this.formatPhoneNumber(phoneNumber, invoiceData.currency);
      this.openWhatsApp(formattedPhone, message);

      return linkData;
    } catch (error) {
      console.error('Error sharing invoice:', error);
      const errorMessage = error.message || 'Unknown error';
      alert(`Failed to share invoice.\n\nError: ${errorMessage}\n\nPlease ensure:\n1. Backend server is running (localhost:3000)\n2. Customer phone number is filled\n3. All required fields are complete`);
      return null;
    }
  }

  /**
   * Share estimate via WhatsApp link
   * @param {Object} estimateData - Complete estimate data
   * @param {string} phoneNumber - Customer phone number
   */
  static async shareEstimate(estimateData, phoneNumber) {
    try {
      // Validate phone number
      if (!phoneNumber || phoneNumber.trim() === '') {
        alert('Please enter a phone number to send via WhatsApp');
        return;
      }

      // Create shareable link
      const linkData = await this.createShareableLink('estimate', estimateData);
      
      if (!linkData.success) {
        throw new Error('Failed to create shareable link');
      }

      // Generate WhatsApp message with link
      const message = this.generateEstimateMessage(estimateData, linkData.shareableUrl);

      // Open WhatsApp with correct country code based on currency
      const formattedPhone = this.formatPhoneNumber(phoneNumber, estimateData.currency);
      this.openWhatsApp(formattedPhone, message);

      return linkData;
    } catch (error) {
      console.error('Error sharing estimate:', error);
      const errorMessage = error.message || 'Unknown error';
      alert(`Failed to share estimate.\n\nError: ${errorMessage}\n\nPlease ensure:\n1. Backend server is running (localhost:3000)\n2. Customer phone number is filled\n3. All required fields are complete`);
      return null;
    }
  }

  /**
   * Create shareable link by saving to backend
   * @param {string} type - 'invoice' or 'estimate'
   * @param {Object} data - Invoice/estimate data
   * @returns {Promise<Object>} Response with shareable URL
   */
  static async createShareableLink(type, data) {
    try {
      const response = await fetch(`${this.API_BASE_URL}/invoices/share`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type,
          currency: data.currency,
          data: {
            // Customer Information
            customerName: data.customerName,
            phoneNumber: data.phoneNumber,
            address: data.address,
            
            // Invoice/Estimate Details
            invoiceNumber: data.invoiceNumber,
            date: data.date,
            validUntil: data.validUntil,
            
            // Item Details
            itemCode: data.itemCode,
            itemName: data.itemName,
            itemType: data.itemType,
            category: data.category,
            subCategory: data.subCategory,

            // Multi-item bills: one entry per line (itemCode, purity, netWeight, amount)
            items: Array.isArray(data.items) ? data.items : [],
            
            // Gold Details
            goldType: data.goldType,
            goldWeight: data.goldWeight,
            goldRate: data.goldRate,
            makingCharges: data.makingCharges,
            goldAmount: data.goldAmount,
            
            // Stone Details
            stoneType: data.stoneType,
            stoneWeight: data.stoneWeight,
            stoneRate: data.stoneRate,
            stoneAmount: data.stoneAmount,
            
            // Other Charges
            otherCharges: data.otherCharges,
            discount: data.discount,
            
            // Totals
            subTotal: data.subTotal,
            cgst: data.cgst,
            sgst: data.sgst,
            totalGst: data.totalGst,
            grandTotal: data.grandTotal,
            
            // Payment
            advancePayment: data.advancePayment,
            balancePayment: data.balancePayment,
            
            // Company Info
            companyName: data.companyName,
            companyAddress: data.companyAddress,
            companyPhone: data.companyPhone,
            companyGSTIN: data.companyGSTIN,
            
            // Additional
            notes: data.notes,
            terms: data.terms
          }
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server error (${response.status}): ${errorText || 'Failed to create shareable link'}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating shareable link:', error);
      if (error.message.includes('fetch')) {
        throw new Error('Cannot connect to server. Please ensure the backend server is running on localhost:3000');
      }
      throw error;
    }
  }

  /**
   * Generate WhatsApp message for invoice with link
   * @param {Object} data - Invoice data
   * @param {string} shareableUrl - Shareable URL
   * @returns {string} Formatted message
   */
  static generateInvoiceMessage(data, shareableUrl) {
    const currencySymbol = data.currency === 'USD' ? '$' : '₹';
    const companyName = data.companyName || 'Jewelry Store';

    let message = `Dear ${data.customerName},\n\n`;
    message += `Thank you for choosing ${companyName}! 💎\n\n`;
    message += `📄 *VIEW YOUR INVOICE*\n`;
    message += `━━━━━━━━━━━━━━━━━━━━\n`;
    message += `${shareableUrl}\n\n`;
    message += `*Invoice Details:*\n`;
    message += Array.isArray(data.items) && data.items.length > 0
      ? `Items: ${data.items.length}\n`
      : `Item Code: ${data.itemCode}\n`;
    message += `Date: ${data.date}\n`;
    message += `Amount: ${currencySymbol} ${this.formatAmount(data.grandTotal)}\n\n`;
    message += `_Click the link above to view the complete invoice with all details._\n\n`;
    message += `Valid for 30 days.\n\n`;
    message += `For any queries, please contact us anytime! 🙏\n\n`;
    message += `${companyName}`;

    return message;
  }

  /**
   * Generate WhatsApp message for estimate with link
   * @param {Object} data - Estimate data
   * @param {string} shareableUrl - Shareable URL
   * @returns {string} Formatted message
   */
  static generateEstimateMessage(data, shareableUrl) {
    const currencySymbol = data.currency === 'USD' ? '$' : '₹';
    const companyName = data.companyName || 'Jewelry Store';

    let message = `Dear ${data.customerName},\n\n`;
    message += `Thank you for your interest in ${companyName}! 💎\n\n`;
    message += `📋 *VIEW YOUR PRICE ESTIMATE*\n`;
    message += `━━━━━━━━━━━━━━━━━━━━\n`;
    message += `${shareableUrl}\n\n`;
    message += `*Estimate Details:*\n`;
    message += `Item Code: ${data.itemCode}\n`;
    message += `Date: ${data.date}\n`;
    if (data.validUntil) {
      message += `Valid Until: ${data.validUntil}\n`;
    }
    message += `Estimated Amount: ${currencySymbol} ${this.formatAmount(data.grandTotal)}\n\n`;
    message += `_Click the link above to view the complete estimate with breakdown._\n\n`;
    message += `_Note: This is an estimate. Final price may vary based on actual weight and current rates._\n\n`;
    message += `Please visit us to confirm your order! 🙏\n\n`;
    message += `${companyName}`;

    return message;
  }

  /**
   * Format phone number for WhatsApp with country code based on currency
   * @param {string} phone - Phone number
   * @param {string} currency - 'INR' or 'USD'
   * @returns {string} Formatted phone number with country code
   */
  static formatPhoneNumber(phone, currency) {
    // Remove all spaces, dashes, parentheses, and dots
    let cleaned = phone.replace(/[\s\-().]/g, '');

    // Remove leading + if present
    cleaned = cleaned.replace(/^\+/, '');

    // Determine country code based on currency
    const countryCode = currency === 'USD' ? '1' : '91';
    
    // If number already starts with the correct country code, return as is
    if (cleaned.startsWith(countryCode)) {
      return cleaned;
    }

    // If number starts with a different country code, return as is (user specified it)
    // Assuming country codes are 1-3 digits followed by 10 digits
    if (cleaned.length > 10) {
      return cleaned;
    }

    // Add appropriate country code for 10-digit numbers
    if (currency === 'USD') {
      // US format: +1 + 10 digit number
      if (cleaned.length === 10) {
        return '1' + cleaned;
      }
    } else {
      // India format: +91 + 10 digit number
      if (cleaned.length === 10) {
        return '91' + cleaned;
      }
    }

    // Return as is if format is unclear
    return cleaned;
  }

  /**
   * Format amount for display
   * @param {string|number} amount - Amount to format
   * @returns {string} Formatted amount
   */
  static formatAmount(amount) {
    if (!amount) return '0.00';
    
    // Remove currency symbols and parse
    const numAmount = typeof amount === 'string' 
      ? parseFloat(amount.replace(/[₹$,]/g, '')) 
      : amount;

    return numAmount.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  /**
   * Open WhatsApp with pre-filled message
   * @param {string} phone - Formatted phone number
   * @param {string} message - Message text
   */
  static openWhatsApp(phone, message) {
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${phone}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  }
}

export default WhatsAppLinkService;
